import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import AppleSignInButton from '../../components/auth/AppleSignInButton';
import PhoneAuthSection from '../../components/auth/PhoneAuthSection';

export default function RegisterPage() {
  const { t } = useTranslation();
  const { isAuthenticated, register, loginWithGoogle, loginWithApple, isFirebaseConfigured } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState(
    () => new URLSearchParams(window.location.search).get('ref') || '',
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/" replace />;

  if (registered) {
    return (
      <div className="form-shell">
        <div className="form-card" style={{ textAlign: 'center' }}>
          <div className="mb-6 h-1.5 w-16 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500" style={{ margin: '0 auto 24px' }} />
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 28, height: 28, color: '#6366F1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
          </div>
          <h1 className="page-title">{t('auth.checkInbox')}</h1>
          <p className="page-subtitle" style={{ marginTop: 8 }}>
            {t('auth.verificationSent', { email })}
          </p>
          <Link
            to="/login"
            className="btn btn-primary"
            style={{ display: 'block', width: '100%', marginTop: 24, textAlign: 'center' }}
          >
            {t('auth.goToSignIn')}
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    setLoading(true);
    const result = await register({ name, email, password, referralCode });
    setLoading(false);
    if (!result.ok) { setError(result.message); return; }
    setRegistered(true);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    const result = await loginWithGoogle();
    setLoading(false);
    if (!result.ok) { setError(result.message); return; }
    navigate('/', { replace: true });
  };

  const handleAppleLogin = async () => {
    setError('');
    setLoading(true);
    const result = await loginWithApple();
    setLoading(false);
    if (!result.ok) { setError(result.message); return; }
    navigate('/', { replace: true });
  };

  return (
    <div className="form-shell">
      <div className="form-card">
        <div className="mb-6 h-1.5 w-16 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500" />

        <span className="pill">{t('auth.getStarted')}</span>

        <h1 className="page-title section-gap">{t('auth.createAccount')}</h1>
        <p className="page-subtitle">{t('auth.saveProgress')}</p>

        <form className="section-gap" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor="name">{t('auth.fullName')}</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              autoComplete="name"
              placeholder={t('auth.fullNamePlaceholder')}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder={t('auth.passwordPlaceholder')}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder={t('auth.confirmPasswordPlaceholder')}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="referralCode">{t('auth.referralCodeLabel')}</label>
            <input
              id="referralCode"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              className="input"
              placeholder={t('auth.referralCodePlaceholder')}
            />
          </div>

          {error && (
            <div className="error-box">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('auth.orContinueWith')}</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="social-stack">
          <button type="button" className="social-btn" onClick={handleGoogleLogin} disabled={loading || !isFirebaseConfigured}>
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.continueWithGoogle')}
          </button>

          <AppleSignInButton
            onClick={handleAppleLogin}
            disabled={loading || !isFirebaseConfigured}
            label={t('auth.continueWithApple')}
          />

          <PhoneAuthSection
            onSuccess={() => navigate('/', { replace: true })}
            disabled={!isFirebaseConfigured}
          />

          {!isFirebaseConfigured && (
            <p className="social-note">{t('auth.firebaseNotConfigured')}</p>
          )}
        </div>

        <p className="muted-link-row">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login">{t('auth.signIn')}</Link>
        </p>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-100" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-300">{t('auth.or')}</span>
          <div className="h-px flex-1 bg-slate-100" />
        </div>

        <Link
          to="/"
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
        >
          {t('auth.continueAsGuest')}
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
